import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { EmailEditorComponent } from "angular-email-editor";
import { saveAs } from "file-saver";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { UserService } from "src/app/core/services/user.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { AppMessageSeverity } from "src/app/shared/models/enums/AppMessageSeverity.enum";
import { MarketingTemplate } from "src/app/shared/models/MarketingTemplate.item";

@Component({
	selector: "app-template-setup",
	templateUrl: "./template-setup.component.html",
	styleUrls: ["../../../commandcenter.scss"],
})
export class TemplateSetupComponent implements OnInit {
	@Input() template: MarketingTemplate;
	@Output() templateChange = new EventEmitter<MarketingTemplate>();

	@Input() templateList: MarketingTemplate[];
	@Output() templateListChange = new EventEmitter<MarketingTemplate[]>();

	@ViewChild("editor") emailEditor: EmailEditorComponent;

	changesToSave: boolean = true;

	editorIsLoaded: boolean = false;

	initialSetup: boolean = false;

	originatingBlastKey: string;

	templateLoaded: boolean = false;
	templateHtml: string;
	templateJson: any;

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		public venueService: OOVenueService,
		private route: ActivatedRoute,
		public userService: UserService
	) {}

	ngOnInit(): void {
		const templateKey = this.route.snapshot.paramMap.get("templateKey");

		if (templateKey) {
			this.initFromRoute(templateKey);
			this.originatingBlastKey = this.route.snapshot.queryParamMap.get("bk");
		} else {
			this.initialSetup = this.template.name ? false : true;
			if (this.template.name) {
				this.getTemplate();
			}
		}
	}

	async initFromRoute(templateKey: string) {
		this.getTemplate(templateKey);
	}

	async getTemplate(templateSk?: string) {
		const resp = await this.focuslink.marketingTemplateGetOne(this.venueService.venueKey, templateSk || this.template.sk);
		this.template = new MarketingTemplate(resp.templateProperties);
		this.templateHtml = resp.templateBody;
		this.templateJson = resp.templateJson;
		this.templateLoaded = true;
		if (this.editorIsLoaded) {
			this.loadSelectedTemplate();
		}
	}

	clearDesign() {
		this.emailEditor.editor.loadBlank();
	}

	editorLoaded(event) {
		this.editorIsLoaded = true;
		if (this.templateJson) {
			this.loadSelectedTemplate();
		}
	}

	exportHtml() {
		this.emailEditor.editor.exportHtml((data) => {
			var blob = new Blob([data.html], { type: "text/html;charset=utf-8" });
			saveAs(blob, `${this.template.name}.html`);
		});
	}

	loadSelectedTemplate() {
		this.emailEditor.loadDesign(JSON.parse(this.templateJson));
	}

	onCancel() {
		if (this.changesToSave) {
			this.app.confirmation.confirm({
				message: `Close without saving changes?`,
				accept: async () => {
					this.templateChange.emit(undefined);
				},
				reject: async () => {
					this.app.confirmation.close();
				},
			});
		} else {
			this.templateChange.emit(undefined);
		}
	}

	async onSave() {
		await this.saveDesign();
		await this.saveHtml();
		this.focuslink.marketingTemplatePost(this.venueService.venueKey, this.template, this.templateJson, this.templateHtml);
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Template saved successfully.` });
		this.changesToSave = false;
	}

	async onDelete(templateKey: string) {
		this.focuslink.marketingTemplateDelete(this.venueService.venueKey, templateKey);
		this.templateList.splice(
			this.templateList.findIndex((i) => i.sk === templateKey),
			1
		);
		this.templateListChange.emit(this.templateList);
		this.templateChange.emit(undefined);
	}

	async saveDesign() {
		return new Promise<void>((resolve, reject) => {
			this.emailEditor.saveDesign((data) => {
				this.templateJson = JSON.stringify(data);
				resolve();
			});
		});
	}

	async saveHtml() {
		return new Promise<void>((resolve, reject) => {
			this.emailEditor.editor.exportHtml((data) => {
				this.templateHtml = data.html;
				resolve();
			});
		});
	}
}
